/* eslint-disable camelcase */
import React, {
  useState, useMemo, useCallback, useEffect, forwardRef, useRef, MouseEvent, FocusEvent, MutableRefObject, KeyboardEvent, FC,
} from 'react';
import loadScript from 'dynamic-script';
import axios from 'axios';
import lowerFirst from 'lodash/lowerFirst';
import noop from 'lodash/noop';

import { PostCanadaAddress } from 'ia-react-core/~/interfaces/PostCanadaAddress';
import { Address } from 'ia-react-core/~/interfaces/Address';
import { Option } from 'ia-react-core/~/interfaces/Option';
import keys from 'ia-react-core/~/utils/accessors/keys';
import { Full } from 'ia-react-core/~/interfaces/Full';
import defaultDeliveryOptions from '../../../constants/DELIVERY_TYPES';
import defaultSubBuildingOptions from '../../../constants/SUB_BUILDING_TYPES';
import Chevron from '../../chevrons/Chevron';
import useTranslation from '../../../hooks/useTranslation';
import AutoSuggestResourceInput from '../AutoSuggestResourceInput';
import getLanguageCode from '../../../utils/language/getLanguageCode';
import { StyledButtonFlag, StyledChangeCountryButton, StyledChangeCountryWrapper, StyledOptionFlag } from './AddressSuggestInput.styles';
import { AutoSuggestResourceInputProps } from '../AutoSuggestResourceInput/AutoSuggestResourceInput';
import { ManualAddressFormProps } from '../ManualAddressForm';
import { CustomContentProps, DropListOption } from '../_DropList/DropList';

export interface PostCanadaFindItem {
  Id: string;
  Next: 'Retrieve' | 'Find';
  Text: string;
  Description: string;
}

interface PostCanadaFindData {
  Items: [PostCanadaFindItem];
}

interface PostCanadaRetrieveData {
  Items: PostCanadaAddress[];
}

export interface AddressSuggestInputProps extends Omit<AutoSuggestResourceInputProps<PostCanadaFindItem | string, PostCanadaFindData>, 'onValueChange' | 'url' | 'method' | 'value'> {
  /** Key for Post Canada API. */
  postCanadaApiKey: string;
  /** Url root of the Post Canada API. */
  postCanadaApiRoot?: string;
    /** Value to use when the autoSuggest is empty. */
  emptyValue?: string;
  /** The version for the script Post Canada api. */
  scriptVersion?: string;
  /** The version for the "find" api of Post Canada. */
  findVersion?: string;
  /** The version for the "retrieve" api of Post Canada. */
  retrieveVersion?: string;
  /** Country code to search the address in. */
  initialCountry?: string;
  /** Url host the Post Canada API. */
  postCanadaHost?: string;
  /** Do we inject or not canada post scripts. True by default */
  injectPostCanadaScript?: boolean;
  /** Array of countries. If not provided, the countries from Post Canada will be used. */
  countries?: Option[];
  /** Callback triggered when the countries are loaded. */
  onCountriesLoad?: (countries: Option[]) => void;
  subBuildingOptions?: ManualAddressFormProps['subBuildingOptions'];
  deliveryOptions?: ManualAddressFormProps['deliveryOptions'];
  onValueChange?: (address: Address | PostCanadaFindItem | string) => void;
  onTouched?: (address: Address | PostCanadaFindItem | string) => void;
  value?: Address | string;
}

export interface AddressSuggestInputStatics {
  scriptLoaded: boolean;
}

const AddressSuggestInputDefaultprops: Partial<AddressSuggestInputProps> = {
  emptyValue: '',
  scriptVersion: '2.30',
  findVersion: '2.10',
  retrieveVersion: 'v2.11',
  initialCountry: 'CA',
  postCanadaHost: 'https://ws1.postescanada-canadapost.ca',
  postCanadaApiRoot: '/AddressComplete/Interactive',
  injectPostCanadaScript: true,
};

const ADDRESS_DEFAULT = {
  street: '',
  countryIso2: '',
  province: '',
  buildingNumber: '',
  subBuilding: '',
  city: '',
  postalCode: '',
  poBoxNumber: '',
  ruralRoute: '',
  place: '',
  type: '',
};

/**
 * @mode developer
 */
const AddressSuggestInput = Object.assign(
  forwardRef<HTMLInputElement, AddressSuggestInputProps>(({
    countries, value, emptyValue, disabled, placeholder, postCanadaApiKey, scriptVersion, findVersion, retrieveVersion, initialCountry, postCanadaHost, postCanadaApiRoot, onCountriesLoad, onValueChange, onSearch, onTouched, subBuildingOptions, deliveryOptions, injectPostCanadaScript, ...props
  }, ref) => {
    const keepOpenedRef = useRef<CustomContentProps['keepOpen']>();
    const searchTimerRef = useRef<NodeJS.Timeout>();
    const clickTimerRef = useRef<NodeJS.Timeout>();
    const { t, language } = useTranslation('Core/AddressSuggestInput');
    const [isScriptLoading, setIsScriptLoading] = useState(true);
    const [isScriptFailed, setIsScriptFailed] = useState(false);
    const [isControlled] = useState(() => typeof value !== 'undefined');
    const [isOpened, setIsOpened] = useState(false);
    const [search, setSearch] = useState<string>(emptyValue);
    const [addressSearch, setAddressSearch] = useState<string>(search);

    const cachedTargetRef = useRef<HTMLElement>();
    const [isChoosingCountry, setIsChoosingCountry] = useState(false);
    const [currentCountry, setCurrentCountry] = useState(initialCountry);

    const addressObjectToText = useCallback((addess: Address): string => (
      [
        [addess.buildingNumber, addess.street, addess.city].filter((p) => p).join(' '),
        addess.province, addess.postalCode].filter((p) => p).join(', ')
    ), []);

    const getDefaultedValue = useCallback(() => {
      if (value && typeof value === 'object') return addressObjectToText(value);
      if (typeof value !== 'undefined') return value as unknown as string;
      return emptyValue;
    }, [addressObjectToText, emptyValue, value]);

    const [lastSearch, setLastSearch] = useState(null);
    const [selectedValue, setSelectedValue] = useState<string>(getDefaultedValue);

    const [error, setError] = useState(null);
    const languageCode = useMemo(() => getLanguageCode(language), [language]);
    const pcLanguageCode = useMemo(() => (languageCode === 'fr') ? 'FRE' : 'ENG', [languageCode]);

    const resolvedSubBuildingOptions = useMemo(() => subBuildingOptions || defaultSubBuildingOptions[languageCode], [languageCode, subBuildingOptions]);
    const resolvedDeliveryOptions = useMemo(() => deliveryOptions || defaultDeliveryOptions[languageCode], [deliveryOptions, languageCode]);

    const urlSearch = useCallback((currentSearch: string) => {
      const searchTerm = encodeURIComponent(currentSearch);
      const lastId = lastSearch ? lastSearch.Id : '';
      return (
        `${postCanadaHost}${postCanadaApiRoot}/Find/${findVersion}/json3.ws?Key=${postCanadaApiKey}&Country=${currentCountry}&SearchTerm=${searchTerm}&LastId=${lastId}&SearchFor=Everything&LanguagePreference=${languageCode}`
      );
    }, [lastSearch, postCanadaHost, postCanadaApiRoot, findVersion, postCanadaApiKey, currentCountry, languageCode]);

    const urlDetails = useCallback((id: string): string => (
      `${postCanadaHost}${postCanadaApiRoot}/Retrieve/${retrieveVersion}/json3.ws?Key=${postCanadaApiKey}&LanguagePreference=${languageCode}&Id=${id}`
    ), [postCanadaHost, postCanadaApiRoot, retrieveVersion, postCanadaApiKey, languageCode]);

    const formatKey = useCallback(<S extends string>(key: S) => lowerFirst(key) as Uncapitalize<S>, []);

    const handleValueChange = useCallback((newValue: PostCanadaFindItem | string) => {
      setError(null);

      if (typeof newValue === 'object' && newValue?.Next === 'Retrieve') {
        axios
          .get<PostCanadaRetrieveData>(urlDetails(newValue.Id))
          .then(({ data }) => {
            const item = data.Items.find((i) => i.Language === pcLanguageCode) || data.Items[0];
            const details = keys(item).reduce((result, key) => {
              if (key === 'POBoxNumber') {
                result.poBoxNumber = item[key];
              } else {
                result[formatKey(key)] = item[key];
              }
              return result;
            }, { ...ADDRESS_DEFAULT } as Partial<Address>);

            if (details.line1?.match(/^RR ([\w]+)/)) {
              const [, ruralRoute] = details.line1.match(/^RR ([\w]+)/);
              details.ruralRoute = ruralRoute;
            } else if (!details.poBoxNumber && details.line1?.match(/^PO[ -]([\d]+)/)) {
              const [, poBoxNumber] = details.line1.match(/^PO[ -]([\d]+)/);
              details.poBoxNumber = poBoxNumber;
            }

            if (details.ruralRoute || details.poBoxNumber) {
              const [, place] = details.line1?.match(/^(?:RR|CP|PO Box|PO)[ -](?:[\w]+) (?:stn|rpo|succ|csp) (.*)/i) || [];
              details.place = place || '';
            }

            if ((details.type.toLocaleLowerCase() === 'commercial' && details.subBuilding) || details.subBuilding?.match(/(suite|ste|st|unit|unite)[ 0-9]?/i)) {
              details.subBuildingType = resolvedSubBuildingOptions[1].value;
            } else if (details.subBuilding) {
              details.subBuildingType = resolvedSubBuildingOptions[0].value;
            } else {
              details.subBuildingType = resolvedSubBuildingOptions[2].value;
            }

            if (details.poBoxNumber) {
              details.deliveryType = resolvedDeliveryOptions[0].value;
            } else if (details.ruralRoute) {
              details.deliveryType = resolvedDeliveryOptions[1].value;
            } else {
              details.deliveryType = resolvedDeliveryOptions[2].value;
            }

            if (!isControlled) setSelectedValue(addressObjectToText(details as Full<typeof details>));
            setLastSearch(null);

            if (onValueChange) onValueChange(details as Full<typeof details>);
          })
          .catch((newError) => {
            setError(newError);
          });
      } else if (typeof newValue === 'object' && newValue?.Next === 'Find') {
        setLastSearch(newValue);
        if (!isControlled) setSelectedValue(emptyValue);
      } else {
        if (!isControlled) setSelectedValue(emptyValue);
        setLastSearch(null);
        if (onValueChange) onValueChange(emptyValue);
      }
    }, [addressObjectToText, emptyValue, formatKey, isControlled, onValueChange, pcLanguageCode, resolvedDeliveryOptions, resolvedSubBuildingOptions, urlDetails]);

    const handleSearch = useCallback((newSearch: string) => {
      searchTimerRef.current = setTimeout(() => {
        setSearch(newSearch);
      }, 100);
      if (onSearch) onSearch(newSearch);
      setError(null);
    }, [onSearch]);

    const handleOpened = useCallback((newIsOpened: boolean) => {
      setIsOpened(newIsOpened);
      if (lastSearch) {
        setLastSearch(null);
      }
    }, [lastSearch]);

    const handleCountryClick = useCallback((
      newCountry: string,
      event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
      { targetRef }: { targetRef: MutableRefObject<HTMLElement>},
    ) => {
      setCurrentCountry(newCountry);
      clickTimerRef.current = setTimeout(() => {
        setIsChoosingCountry(false);
        clickTimerRef.current = setTimeout(() => {
          targetRef.current?.focus();
        }, 200);
      });
    }, []);

    const loadPostCanadaScript = useCallback(async () => {
      if (!AddressSuggestInput.scriptLoaded && injectPostCanadaScript) {
        try {
          await loadScript(`${postCanadaHost}/js/addresscomplete-${scriptVersion}.min.js?key=${postCanadaApiKey}&culture=fr`);
          AddressSuggestInput.scriptLoaded = true;
          setIsScriptLoading(false);
        } catch {
          setIsScriptFailed(true);
        }
      } else {
        setIsScriptLoading(false);
      }
    }, [postCanadaApiKey, postCanadaHost, scriptVersion, injectPostCanadaScript]);

    const transformResponse = useCallback(({ Items }: PostCanadaFindData) => Items.map((item) => ({
      label: [item.Text, item.Description].filter((a) => a).join(' '),
      value: item,
      key: ['Id', 'Description'],
      isSearch: item.Next === 'Find',
    })), []);

    const shouldStayOpened = useCallback((item: PostCanadaFindItem | string) => (
      typeof item === 'object' && item?.Next === 'Find'
    ), []);

    const handleTouched = useCallback((newValue: Address | PostCanadaFindItem | string) => {
      // Leaving on a category result, so clearing the display value
      if (typeof newValue === 'object' && 'Next' in newValue) {
        setAddressSearch('');
      }
      if (onTouched) onTouched(newValue as Address | PostCanadaFindItem);
    }, [onTouched]);

    useEffect(() => {
      if (isControlled && value !== selectedValue) {
        setSelectedValue(getDefaultedValue());
      }
    }, [getDefaultedValue, isControlled, selectedValue, value]);

    useEffect(() => {
      loadPostCanadaScript().catch(noop);
    }, [loadPostCanadaScript]);

    const countryOptions = useMemo(() => {
      if (isScriptLoading) return undefined;
      return window.pca.countries
        .filter(({ iso2 }) => !countries || countries.find(({ value: optionValue }) => optionValue === iso2))
        .map(({ flag, iso2, name, name_fr }) => ({
          icon: <StyledOptionFlag flag={flag} />,
          label: languageCode === 'fr' ? name_fr : name,
          value: iso2,
          onClick: handleCountryClick,
        }));
    }, [countries, handleCountryClick, isScriptLoading, languageCode]);

    const optionsOverride: DropListOption<string>[] = useMemo(() => {
      if (isChoosingCountry) return countryOptions;
      return undefined;
    }, [countryOptions, isChoosingCountry]);

    const currentOption = useMemo(() => {
      if (isScriptLoading) return undefined;
      return window.pca.countries.find(({ iso2 }) => iso2 === currentCountry);
    }, [currentCountry, isScriptLoading]);

    const valueToRestore = useMemo(() => {
      if (isChoosingCountry && selectedValue) return '';
      if (!isChoosingCountry) return addressSearch;
      return undefined;
    }, [addressSearch, isChoosingCountry, selectedValue]);

    const resolvedPlaceholder = useMemo(() => {
      if (isScriptFailed) return t('failed');
      if (isScriptLoading) return t('loading');
      return placeholder;
    }, [isScriptFailed, isScriptLoading, placeholder, t]);

    const customContent = useCallback(({ keepOpen, triggerBlur, targetRef }: CustomContentProps) => {
      keepOpenedRef.current = keepOpen;
      cachedTargetRef.current = targetRef.current;
      const handleCountryButtonClick = (event: MouseEvent<any>) => {
        keepOpen(event);
        setIsChoosingCountry((currentCoosingCountry) => !currentCoosingCountry);
      };
      const handleCountryButtonFocus = (event: FocusEvent<any>) => {
        keepOpen(event, { focus: false });
      };
      const handleCountryButtonBlur = () => {
        triggerBlur();
      };
      return (
        <StyledChangeCountryWrapper key="country">
          <StyledChangeCountryButton
            onFocus={handleCountryButtonFocus}
            onBlur={handleCountryButtonBlur}
            onClick={handleCountryButtonClick}
          >
            {t('changeCountry')}<StyledButtonFlag flag={currentOption?.flag} /><Chevron isOpened={isChoosingCountry} />
          </StyledChangeCountryButton>
        </StyledChangeCountryWrapper>
      );
    }, [t, currentOption?.flag, isChoosingCountry]);

    useEffect(() => {
      if (isChoosingCountry) {
        setAddressSearch(search);
        setSearch('');
      } else {
        setAddressSearch(undefined);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChoosingCountry]);

    useEffect(() => {
      if (!isOpened) {
        setIsChoosingCountry(false);
      }
    }, [isOpened]);

    useEffect(() => {
      if (countryOptions && onCountriesLoad) {
        onCountriesLoad(countryOptions.map(({ label, value: val }) => ({
          label,
          value: val,
        })));
      }
    }, [countryOptions, onCountriesLoad]);

    useEffect(() => () => {
      clearTimeout(searchTimerRef.current);
      clearTimeout(clickTimerRef.current);
    }, []);

    return (
      <AutoSuggestResourceInput<PostCanadaFindItem | string, PostCanadaFindData>
        value={!isChoosingCountry ? selectedValue : ''}
        url={urlSearch}
        {...props}
        valueToRestore={valueToRestore}
        optionsOverride={optionsOverride}
        additionalError={error}
        transformResponse={transformResponse}
        shouldStayOpened={shouldStayOpened}
        ref={ref}
        placeholder={resolvedPlaceholder}
        disabled={disabled || isScriptLoading}
        customContent={customContent}
        onValueChange={handleValueChange}
        onSearch={handleSearch}
        onOpened={handleOpened}
        onTouched={handleTouched}
      />
    );
  }) as FC<AddressSuggestInputProps>,
  {
    scriptLoaded: false,
  },
);

AddressSuggestInput.defaultProps = AddressSuggestInputDefaultprops;

export default AddressSuggestInput;
